import FiraCodeWoff2 from "../assets/fonts/FiraCode-latin.woff2";
import JetBrainsMonoWoff2 from "../assets/fonts/JetBrainsMono-latin.woff2";
import SourceCodeProWoff2 from "../assets/fonts/SourceCodePro-latin.woff2";
import UbuntuMonoBoldWoff2 from "../assets/fonts/UbuntuMono-Bold-latin.woff2";
import UbuntuMonoRegularWoff2 from "../assets/fonts/UbuntuMono-Regular-latin.woff2";
import type { CodeFont } from "../types";

/** localStorage key used to persist the user's selected code font. */
export const FONT_STORAGE_KEY = "techdocs.addons.settings.code-font";

/** Label for the browser-default monospace font option. */
export const FONT_DEFAULT = "Default";

/** Custom event name dispatched when the active code font changes. */
export const FONT_CHANGE_EVENT = "code-font-change";

/** A single `@font-face` weight variant for a {@link FontFaceConfig}. */
interface FontFaceEntry {
    /** CSS `font-weight` value (e.g. `"400 500"` or `"700"`). */
    weight: string;
    /** Resolved URL to the `.woff2` font file. */
    url: string;
}

/**
 * Configuration for one bundled code font family.
 * Drives both the picker list ({@link CODE_FONTS}) and the stylesheet ({@link FONT_FACE_CSS}).
 */
interface FontFaceConfig {
    /** Human-readable family name shown in the font picker UI. */
    family: string;
    /** CSS `font-family` value used in inline styles. */
    cssFamily: string;
    /** One or more weight variants that make up the font family. */
    faces: FontFaceEntry[];
}

/** Registry of all bundled code fonts. Drives both {@link CODE_FONTS} and {@link FONT_FACE_CSS}. */
const FONT_CONFIGS: FontFaceConfig[] = [
    {
        family: "Fira Code",
        cssFamily: "'Fira Code', monospace",
        faces: [{ weight: "400 500", url: FiraCodeWoff2 }],
    },
    {
        family: "JetBrains Mono",
        cssFamily: "'JetBrains Mono', monospace",
        faces: [{ weight: "400 500", url: JetBrainsMonoWoff2 }],
    },
    {
        family: "Source Code Pro",
        cssFamily: "'Source Code Pro', monospace",
        faces: [{ weight: "400 500", url: SourceCodeProWoff2 }],
    },
    {
        family: "Ubuntu Mono",
        cssFamily: "'Ubuntu Mono', monospace",
        faces: [
            { weight: "400", url: UbuntuMonoRegularWoff2 },
            { weight: "700", url: UbuntuMonoBoldWoff2 },
        ],
    },
];

/** Ordered list of selectable code fonts presented in the font picker UI. */
export const CODE_FONTS: CodeFont[] = [
    { label: "Default", value: "" },
    ...FONT_CONFIGS.map(c => ({ label: c.family, value: c.cssFamily })),
];

/**
 * CSS string containing all `@font-face` declarations for the bundled code fonts.
 *
 * @remarks
 * Must be injected into `document.head`, not the TechDocs shadow DOM, because
 * `@font-face` rules are ignored when scoped inside a shadow root.
 */
export const FONT_FACE_CSS = FONT_CONFIGS.flatMap(c =>
    c.faces.map(
        f => `
@font-face {
    font-family: '${c.family}';
    font-style: normal;
    font-weight: ${f.weight};
    font-display: swap;
    src: url(${f.url}) format('woff2');
}`
    )
).join("\n");
